import { useMemo } from 'react';
import { Chip } from '@nextui-org/react';
import { SidebarIcon as Icon } from './SidebarIcon';

import { type SidebarItem, SidebarItemType } from './SidebarTypes';
import TeamAvatar from './TeamAvatar';
import { useTranslation } from '../../../../../common/src/hooks/useTranslation';
import useDataStore from '../../../../../common/src/stores/useDataStore';
import { FINDTEU_TRACKING_ENABLED } from 'common/src/utils/tracking-providers';
import useUserStore from '../../../../../common/src/stores/useUserStore';
import useSettlementInboxAccess from '../../../hooks/useSettlementInboxAccess';
import useMyShipmentCaseCount from '../../../hooks/useMyShipmentCaseCount';

/**
 * Please check the https://nextui.org/docs/guide/routing to have a seamless router integration
 */

export const items: SidebarItem[] = [
  {
    key: 'home',
    href: '#',
    icon: 'tabler:home',
    title: 'Home',
  },
  {
    key: 'projects',
    href: '#',
    icon: 'tabler:folder',
    title: 'Projects',
    endContent: (
      <Icon className='text-default-400' icon='tabler:plus' width={24} />
    ),
  },
  {
    key: 'tasks',
    href: '#',
    icon: 'tabler:checklist',
    title: 'Tasks',
    endContent: (
      <Icon className='text-default-400' icon='tabler:plus' width={24} />
    ),
  },
  {
    key: 'team',
    href: '#',
    icon: 'tabler:users',
    title: 'Team',
  },
  {
    key: 'tracker',
    href: '#',
    icon: 'tabler:clock',
    title: 'Tracker',
    endContent: (
      <Chip size='sm' variant='flat'>
        New
      </Chip>
    ),
  },
  {
    key: 'analytics',
    href: '#',
    icon: 'tabler:chart-line',
    title: 'Analytics',
  },
  {
    key: 'perks',
    href: '#',
    icon: 'tabler:gift',
    title: 'Perks',
    endContent: (
      <Chip size='sm' variant='flat'>
        3
      </Chip>
    ),
  },
  {
    key: 'expenses',
    href: '#',
    icon: 'tabler:receipt',
    title: 'Expenses',
  },
  {
    key: 'settings',
    href: '#',
    icon: 'tabler:settings',
    title: 'Settings',
  },
];

export const superadminItems: SidebarItem[] = [
  {
    key: 'superadmin',
    title: '',
    items: [
      // {
      //   key: 'superadmin-dashboard',
      //   href: '/superadmin-dashboard',
      //   icon: 'solar:shield-user-outline',

      //   title: 'Superadmin',
      // },
      {
        key: 'superadmin-clients',
        href: '/superadmin-clients',
        icon: 'tabler:users',
        title: 'Seguimiento Clientes',
      },
      {
        key: 'superadmin-api-access',
        href: '/superadmin-api-access',
        icon: 'tabler:api',
        title: 'Acceso API',
      },
      {
        key: 'superadmin-analytics',
        href: '/superadmin-analytics',
        icon: 'tabler:chart-bar',
        title: 'Estadísticas',
      },
      {
        key: 'superadmin-exporters',
        href: '/superadmin-exporters',
        icon: 'tabler:building-factory-2',
        title: 'Exportadoras',
      },
      {
        key: 'superadmin-inbox-addresses',
        href: '/superadmin-inbox-addresses',
        icon: 'tabler:mail',
        title: 'Correos de liquidaciones',
      },
      {
        key: 'superadmin-importers',
        href: '/superadmin-importers',
        icon: 'tabler:building-store',
        title: 'Importadores',
      },
      {
        key: 'superadmin-people',
        href: '/superadmin-people',
        icon: 'tabler:users-group',
        title: 'Usuarios',
      },
      {
        key: 'superadmin-chat',
        href: '/superadmin-chat',
        icon: 'tabler:message-chatbot',
        title: 'Asistente IA',
      },
      {
        key: 'superadmin-updates',
        href: '/superadmin-updates',
        icon: 'tabler:bell-ringing',
        title: 'Actualizaciones',
      },
      {
        key: 'superadmin-documents',
        href: '/superadmin-documents',
        icon: 'tabler:files',
        title: 'Documentos',
      },
      {
        key: 'superadmin-volume',
        href: '/superadmin-volume',
        icon: 'tabler:chart-bar',
        title: 'Volumen industria',
      },
      {
        key: 'superadmin-shipments',
        href: '/superadmin-shipments',
        icon: 'tabler:truck-delivery',
        title: 'Despachos',
      },
      {
        key: 'superadmin-containers',

        icon: 'tabler:cube-send',
        title: 'Tracking Contenedores',
        type: SidebarItemType.Nest,
        items: [
          // "Trackear" usa FindTEU, hoy desactivado (ver tracking-providers.ts).
          ...(FINDTEU_TRACKING_ENABLED
            ? ([
                {
                  key: 'superadmin-containers-tracking',
                  href: '/superadmin-containers-tracking',
                  icon: 'tabler:eye',
                  title: 'Trackear',
                },
              ] satisfies SidebarItem[])
            : []),
          {
            key: 'superadmin-containers-summary',
            href: '/superadmin-containers-summary',
            icon: 'tabler:chart-bar',
            title: 'Resumen',
          },
          {
            key: 'superadmin-shipsgo-tracking',
            href: '/superadmin-shipsgo-tracking',
            icon: 'tabler:ship',
            title: 'ShipsGo',
          },
        ],
      },

      {
        key: 'superadmin-emails',
        href: '/superadmin-emails',
        icon: 'tabler:mail',
        title: 'Emails',
      },
      {
        key: 'superadmin-reports',
        // href: '/superadmin-reports',
        icon: 'tabler:report',
        title: 'Newsletter',
        type: SidebarItemType.Nest,
        items: [
          {
            key: 'superadmin-reports-create',
            href: '/superadmin-reports',
            icon: 'tabler:list-check',
            title: 'Detalle',
          },
          {
            key: 'superadmin-reports-create',
            href: '/superadmin-reports/create',
            icon: 'tabler:plus',
            title: 'Crear Reporte',
          },
        ],
      },
      {
        key: 'superadmin-settlements',
        href: '/superadmin-settlements',
        icon: 'tabler:calculator',
        title: 'Liquidaciones',
      },
      {
        key: 'superadmin-ships',
        href: '/superadmin-ships',
        icon: 'tabler:ship',
        title: 'Naves',
      },
      {
        key: 'superadmin-species',
        href: '/superadmin-species',
        icon: 'tabler:leaf',
        title: 'Especies',
      },
      {
        key: 'superadmin-retail-skus',
        href: '/superadmin-retail-skus',
        icon: 'tabler:tag',
        title: 'SKUs Retail',
      },
      {
        key: 'superadmin-markets',
        href: '/superadmin-markets',
        icon: 'tabler:map-pin',
        title: 'Mercados',
      },
      {
        key: 'superadmin-currencies',
        href: '/superadmin-currencies',
        icon: 'tabler:coin',
        title: 'Monedas',
      },
      {
        key: 'superadmin-settings',
        href: '/superadmin-settings',
        icon: 'tabler:settings',
        title: 'Configuración',
      },
      {
        key: 'superadmin-sharing-permissions',
        href: '/superadmin-sharing-permissions',
        icon: 'tabler:shield-lock',
        title: 'Permisos Industria',
      },
      {
        key: 'superadmin-receivers',
        href: '/superadmin-receivers',
        icon: 'tabler:template',
        title: 'Plantillas',
      },
      {
        key: 'superadmin-generate-integration',
        href: '/superadmin-generate-integration',
        icon: 'tabler:code',
        title: 'Generar Integracion',
      },
      {
        key: 'superadmin-ai-examples',
        href: '/superadmin-ai-examples',
        icon: 'tabler:robot',
        title: 'Ejemplos AI',
      },

      // {
      //   key: 'superadmin-excel-rules',
      //   href: '/superadmin-excel-rules',
      //   icon: 'solar:ruler-outline',
      //   title: 'Reglas Excel',
      // },
    ],
  },
];

export const documentHelperItems: SidebarItem[] = [
  {
    key: 'superadmin',
    title: '',
    items: [
      {
        key: 'superadmin-documents',
        href: '/superadmin-documents',
        icon: 'tabler:files',
        title: 'Documentos',
      },
      {
        key: 'superadmin-retail-skus',
        href: '/superadmin-retail-skus',
        icon: 'solar:cart-large-2-outline',
        title: 'SKUs Retail',
      },
    ],
  },
];

export const useSectionItems = (includeInbox = true): SidebarItem[] => {
  const myCaseCount = useMyShipmentCaseCount();
  const inbox = useSettlementInboxAccess();
  const inboxEnabled = includeInbox && inbox.enabled;
  const inboxPending = includeInbox ? inbox.pending : 0;
  const { t } = useTranslation('sidebar');
  const { selectedSpecies } = useDataStore();
  const { userProfile } = useUserStore();
  const isCherry = selectedSpecies?.id === 2;
  const isDemoExporter =
    userProfile?.exporter?.name?.trim().toUpperCase() === 'DEMO';

  return useMemo<SidebarItem[]>(
    () => [
      {
        key: 'overview',
        title: '',
        items: [
          {
            key: 'dashboard',
            icon: 'tabler:home-2',
            title: t('Dashboard'),
            href: '/dashboard',
            readAccess: ['Liquidaciones', 'Despachos', 'Plan Comercial'],
          },
          {
            key: 'daily-market',
            icon: 'tabler:world',
            title: t('Mercado'),
            type: SidebarItemType.Nest,
            items: [
              {
                key: 'daily-market',
                icon: 'tabler:calendar-event',
                href: '/daily-market',
                title: isCherry ? t('Diario') : t('Dispersión'),
              },
              {
                key: 'evolution-market',
                href: '/evolution-market',
                icon: 'tabler:trending-up',
                title: t('Evolución'),
              },
              {
                key: 'usda-market-explorer',
                href: '/usda-market-explorer',
                icon: 'tabler:world-dollar',
                title: t('USDA'),
              },
              {
                key: 'retail-market',
                href: '/retail-market',
                icon: 'tabler:building-store',
                title: t('Retail'),
              },
              {
                key: 'logbook',
                href: '/logbook',
                icon: 'tabler:notebook',
                title: t('Bitácora'),
                readAccess: ['Bitácora'],
                writeAccess: ['Bitácora'],
              },
            ],
          },
          {
            key: 'shipments',
            title: t('Despachos'),
            icon: 'tabler:package',
            type: SidebarItemType.Nest,
            items: [
              {
                key: 'shipments-summary',
                permissionKey: 'shipments-details',
                icon: 'tabler:chart-pie',
                href: '/shipments/summary',
                title: t('Resumen'),
                readAccess: ['Liquidaciones', 'Despachos', 'Configuración'],
                writeAccess: ['Despachos', 'Configuración'],
              },
              {
                key: 'shipments-details',
                icon: 'tabler:list-check',
                href: '/shipments/details',
                title: t('Detalle'),
                readAccess: ['Liquidaciones', 'Despachos', 'Configuración'],
                writeAccess: ['Despachos', 'Configuración'],
              },
              ...(isDemoExporter
                ? ([
                    {
                      key: 'shipments-frescura',
                      icon: 'tabler:leaf',
                      href: '/shipments/frescura',
                      title: t('Frescura'),
                      // Keep the existing beta access scope: admin users only.
                      readAccess: [],
                      badge: 'Beta',
                    },
                  ] satisfies SidebarItem[])
                : []),
              {
                key: 'shipments-arrivals',
                permissionKey: 'shipments-details',
                icon: 'tabler:calendar-time',
                href: '/shipments/arrivals',
                title: t('Gestor de Arribos'),
                readAccess: ['Liquidaciones', 'Despachos', 'Configuración'],
                badge: 'Beta',
              },
              {
                key: 'shipments-schedule',
                permissionKey: 'shipments-details',
                icon: 'tabler:timeline-event',
                href: '/shipments/schedule',
                title: t('Cronograma de Embarques'),
                readAccess: ['Liquidaciones', 'Despachos', 'Configuración'],
                badge: 'Beta',
              },
              {
                key: 'commercial-plan',
                icon: 'tabler:contract',
                href: '/commercial-plan/summary',
                title: t('Programa Comercial'),
                readAccess: [
                  'Liquidaciones',
                  'Despachos',
                  'Plan Comercial',
                  'Configuración',
                ],
                writeAccess: ['Plan Comercial', 'Configuración'],
              },
            ],
          },
          {
            key: 'shipment-cases',
            permissionKey: 'claims',
            title: t('Reclamos'),
            icon: 'tabler:alert-triangle',
            type: SidebarItemType.Nest,
            items: [
              {
                key: 'shipment-cases-summary',
                permissionKey: 'claims',
                icon: 'tabler:chart-pie',
                href: '/claims/summary',
                title: t('Resumen'),
                readAccess: ['Reclamos'],
                writeAccess: ['Reclamos'],
              },
              {
                key: 'shipment-cases-details',
                permissionKey: 'claims',
                icon: 'tabler:list-check',
                href: '/claims/details',
                title: t('Detalle'),
                readAccess: ['Reclamos'],
                writeAccess: ['Reclamos'],
              },
              {
                key: 'shipment-cases-my-actions',
                permissionKey: 'claims',
                icon: 'tabler:list-check',
                href: '/claims/my-actions',
                title: t('Mis gestiones'),
                pendingCount: myCaseCount,
                readAccess: ['Reclamos'],
                writeAccess: ['Reclamos'],
              },
            ],
          },
          {
            key: 'liquidaciones',
            title: t('Liquidaciones Clientes'),
            icon: 'tabler:cash-banknote',
            type: SidebarItemType.Nest,
            items: [
              {
                key: 'settlements-sumarry',
                icon: 'tabler:chart-pie',
                href: '/settlements/summary',
                title: t('Resumen'),
                readAccess: ['Liquidaciones'],
              },
              {
                key: 'Detalle',
                icon: 'tabler:list-check',
                href: '/settlements/details',
                title: t('Detalle'),
                readAccess: ['Liquidaciones', 'Despachos'],
                writeAccess: ['Liquidaciones'],
              },
              ...(inboxEnabled
                ? ([
                    {
                      key: 'settlement-inbox',
                      permissionKey: 'Detalle',
                      icon: 'tabler:inbox',
                      href: '/settlements/inbox',
                      title: t('Bandeja de Entrada'),
                      endContent:
                        inboxPending > 0 ? (
                          <Chip
                            size='sm'
                            variant='flat'
                            aria-label={`${inboxPending} ${t('documentos pendientes')}`}
                          >
                            {inboxPending}
                          </Chip>
                        ) : null,
                    },
                  ] satisfies SidebarItem[])
                : []),
              {
                key: 'comparative-clients',
                icon: 'tabler:users',
                href: '/settlements/comparative-clients',
                title: t('Comparativo Clientes'),
                readAccess: ['Liquidaciones', 'Reclamos'],
                writeAccess: ['Reclamos'],
              },

              {
                key: 'claims',
                icon: 'tabler:moneybag',
                href: '/settlements/claims',
                title: t('Compensaciones'),
                readAccess: ['Liquidaciones', 'Reclamos'],
                writeAccess: ['Reclamos'],
              },
              {
                key: 'siniestros',
                icon: 'tabler:alert-triangle',
                href: '/settlements/siniestros',
                title: t('Siniestros'),
                readAccess: ['Liquidaciones', 'Reclamos'],
                writeAccess: ['Reclamos'],
                // Beta gate — only these exporter_ids see the item. Admins /
                // superadmins bypass automatically. Remove this when GA.
                // allowedExporterIds: [1, 6, 7, 92, 97, 99, 118],
              },
              {
                key: 'comparative-seasons',
                icon: 'tabler:calendar-stats',
                href: '/settlements/comparative-seasons',
                title: t('Vs Temporadas Anteriores'),
                readAccess: ['Liquidaciones'],
              },
            ],
          },
          {
            key: 'comparativos-fob',
            title: t('Industria'),
            icon: 'tabler:eye-dollar',
            type: SidebarItemType.Nest,
            items: [
              {
                // Solo para exportadoras suscritas a ASOEX (ver filtro en sidebars/index.tsx).
                key: 'industry-volume-group',
                title: t('Volumen'),
                icon: 'tabler:ship',
                type: SidebarItemType.Group,
                items: [
                  {
                    key: 'industry-volume',
                    // El permiso de usuario basic es el del grupo (Equipo los arma por grupo).
                    permissionKey: 'industry-volume-group',
                    icon: 'tabler:chart-line',
                    href: '/industry-volume',
                    title: t('Volumen Exportación'),
                  },
                ],
              },
              {
                key: 'industry-tools',
                title: t('Herramientas'),
                icon: 'tabler:tool',
                type: SidebarItemType.Group,
                items: [
                  {
                    key: 'comparative-industry',
                    icon: 'tabler:building-factory-2',
                    href: '/settlements/comparative-industry',
                    title: t('Comparativo Industria'),
                    readAccess: ['Liquidaciones', 'Reclamos'],
                    writeAccess: ['Reclamos'],
                  },
                ],
              },
              {
                key: 'industry-results',
                title: t('Resultados'),
                icon: 'tabler:chart-bar',
                type: SidebarItemType.Group,
                items: [
                  {
                    key: 'comparative-exporters-unified',
                    icon: 'tabler:currency-dollar',
                    href: '/settlements/comparative-exporters-unified',
                    title: t('FOB/CIF'),
                    readAccess: ['Resultados vs Industria'],
                  },
                  {
                    key: 'comparative-exporters-sales',
                    icon: 'tabler:coins',
                    href: '/settlements/comparative-exporters-sales',
                    title: t('Precios'),
                    readAccess: ['Precios vs. Industria'],
                  },
                  {
                    key: 'comparative-exporters-costs',
                    icon: 'solar:calculator-outline',
                    href: '/settlements/comparative-exporters-costs',
                    title: t('Costos'),
                    readAccess: ['Costos vs. Industria'],
                  },
                ],
              },
              {
                key: 'industry-qualitative',
                title: t('Cualitativo'),
                icon: 'tabler:list-details',
                type: SidebarItemType.Group,
                items: [
                  {
                    key: 'production-industry',
                    icon: 'tabler:chart-histogram',
                    href: '/settlements/liquidated-mix',
                    title: t('Mix Liquidado'),
                    readAccess: ['Resultados vs Industria'],
                  },
                ],
              },
            ],
          },
          {
            key: 'pre-settlements',
            title: t('Preliquidación Productor'),
            icon: 'tabler:calculator',
            href: '/pre-settlements',
            readAccess: ['Liquidaciones', 'Despachos', 'Configuración'],
            writeAccess: ['Configuración'],
          },
          {
            // Reportes para clientes (link y PDF). La clave es la misma que
            // exige la ruta para usuarios basic.
            key: 'client-reports',
            title: t('Reportes'),
            icon: 'tabler:file-analytics',
            href: '/client-reports',
            new: true,
            readAccess: ['Liquidaciones', 'Despachos'],
          },
          {
            key: 'settings',
            icon: 'tabler:settings',
            href: '/settings',
            title: t('Configuración'),
            readAccess: ['Liquidaciones', 'Despachos', 'Configuración'],
            writeAccess: ['Configuración'],
          },
        ],
      },
    ],
    [isCherry, isDemoExporter, t, inboxEnabled, inboxPending, myCaseCount]
  );
};

// Keep original sectionItems as array for backwards compatibility
export const sectionItems: SidebarItem[] = [];

export const useSectionItemsWithTeams = (): SidebarItem[] => {
  return useSectionItems();
};

// Alias for backwards compatibility
export const sectionItemsWithTeams = sectionItems;

export const brandItems: SidebarItem[] = [
  {
    key: 'overview',
    title: 'Overview',
    items: [
      {
        key: 'home',
        href: '#',
        icon: 'tabler:home',
        title: 'Home',
      },
      {
        key: 'projects',
        href: '#',
        icon: 'tabler:folder',
        title: 'Projects',
        endContent: (
          <Icon
            className='text-primary-foreground/60'
            icon='tabler:plus'
            width={24}
          />
        ),
      },
      {
        key: 'tasks',
        href: '#',
        icon: 'tabler:checklist',
        title: 'Tasks',
        endContent: (
          <Icon
            className='text-primary-foreground/60'
            icon='tabler:plus'
            width={24}
          />
        ),
      },
      {
        key: 'team',
        href: '#',
        icon: 'tabler:users',
        title: 'Team',
      },
      {
        key: 'tracker',
        href: '#',
        icon: 'tabler:clock',
        title: 'Tracker',
        endContent: (
          <Chip
            className='bg-primary-foreground font-medium text-primary'
            size='sm'
            variant='flat'
          >
            New
          </Chip>
        ),
      },
    ],
  },
  {
    key: 'your-teams',
    title: 'Your Teams',
    items: [
      {
        key: 'nextui',
        href: '#',
        title: 'NextUI',
        startContent: (
          <TeamAvatar
            classNames={{
              base: 'border-1 border-primary-foreground/20',
              name: 'text-primary-foreground/80',
            }}
            name='Next UI'
          />
        ),
      },
      {
        key: 'tailwind-variants',
        href: '#',
        title: 'Tailwind Variants',
        startContent: (
          <TeamAvatar
            classNames={{
              base: 'border-1 border-primary-foreground/20',
              name: 'text-primary-foreground/80',
            }}
            name='Tailwind Variants'
          />
        ),
      },
      {
        key: 'nextui-pro',
        href: '#',
        title: 'NextUI Pro',
        startContent: (
          <TeamAvatar
            classNames={{
              base: 'border-1 border-primary-foreground/20',
              name: 'text-primary-foreground/80',
            }}
            name='NextUI Pro'
          />
        ),
      },
    ],
  },
];

export const sectionLongList: SidebarItem[] = [
  ...sectionItems,
  {
    key: 'payments',
    title: 'Payments',
    items: [
      {
        key: 'payroll',
        href: '#',
        title: 'Payroll',
        icon: 'tabler:wallet',
      },
      {
        key: 'invoices',
        href: '#',
        title: 'Invoices',
        icon: 'tabler:file-invoice',
      },
      {
        key: 'billing',
        href: '#',
        title: 'Billing',
        icon: 'tabler:receipt',
      },
      {
        key: 'payment-methods',
        href: '#',
        title: 'Payment Methods',
        icon: 'tabler:credit-card',
      },
      {
        key: 'payouts',
        href: '#',
        title: 'Payouts',
        icon: 'tabler:arrows-transfer-down',
      },
    ],
  },
  {
    key: 'your-teams',
    title: 'Your Teams',
    items: [
      {
        key: 'nextui',
        href: '#',
        title: 'NextUI',
        startContent: <TeamAvatar name='Next UI' />,
      },
      {
        key: 'tailwind-variants',
        href: '#',
        title: 'Tailwind Variants',
        startContent: <TeamAvatar name='Tailwind Variants' />,
      },
      {
        key: 'nextui-pro',
        href: '#',
        title: 'NextUI Pro',
        startContent: <TeamAvatar name='NextUI Pro' />,
      },
    ],
  },
];

export const sectionNestedItems: SidebarItem[] = [
  {
    key: 'home',
    href: '#',
    icon: 'tabler:home',
    title: 'Home',
  },
  {
    key: 'projects',
    href: '#',
    icon: 'tabler:folder',
    title: 'Projects',
    endContent: (
      <Icon className='text-default-400' icon='tabler:plus' width={24} />
    ),
  },
  {
    key: 'tasks',
    href: '#',
    icon: 'tabler:checklist',
    title: 'Tasks',
    endContent: (
      <Icon className='text-default-400' icon='tabler:plus' width={24} />
    ),
  },
  {
    key: 'team',
    href: '#',
    icon: 'tabler:users',
    title: 'Team',
  },
  {
    key: 'tracker',
    href: '#',
    icon: 'tabler:clock',
    title: 'Tracker',
    endContent: (
      <Chip size='sm' variant='flat'>
        New
      </Chip>
    ),
  },
  {
    key: 'analytics',
    href: '#',
    icon: 'tabler:chart-line',
    title: 'Analytics',
  },
  {
    key: 'perks',
    href: '#',
    icon: 'tabler:gift',
    title: 'Perks',
    endContent: (
      <Chip size='sm' variant='flat'>
        3
      </Chip>
    ),
  },
  {
    key: 'cap_table',
    title: 'Cap Table',
    icon: 'tabler:chart-pie',
    type: SidebarItemType.Nest,
    items: [
      {
        key: 'shareholders',
        icon: 'tabler:users',
        href: '#',
        title: 'Shareholders',
      },
      {
        key: 'note_holders',
        icon: 'tabler:file-text',
        href: '#',
        title: 'Note Holders',
      },
      {
        key: 'transactions_log',
        icon: 'tabler:history',
        href: '#',
        title: 'Transactions Log',
      },
    ],
  },
  {
    key: 'expenses',
    href: '#',
    icon: 'tabler:receipt',
    title: 'Expenses',
  },
];
